import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useLocation } from "react-router-dom";

/**
 * Global help chat — floating button that opens a Claude-powered panel.
 * Available on every authenticated route.
 */
export default function HelpChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("iqes-help-msgs") || "[]");
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("iqes-help-sid");
    if (!s) { s = `help-${crypto.randomUUID()}`; localStorage.setItem("iqes-help-sid", s); }
    return s;
  });
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem("iqes-help-msgs", JSON.stringify(messages.slice(-30)));
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setSending(true);
    setMessages(m => [...m, { role: "user", text: q, at: Date.now() }]);
    setInput("");
    try {
      const r = await api.post("/help/ask", {
        question: q,
        session_id: sessionId,
        context: { page_route: location.pathname },
      });
      setMessages(m => [...m, { role: "assistant", text: r.data.answer, at: Date.now() }]);
    } catch (e) {
      setMessages(m => [...m, {
        role: "assistant",
        text: `Error: ${e?.response?.data?.detail || "no pude responder"}`,
        at: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Hide on public/auth pages
  if (location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/signup") ||
      location.pathname.startsWith("/public/")) {
    return null;
  }

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="help-chat-toggle"
        onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#003366] text-white shadow-2xl
                   hover:bg-[#002244] hover:scale-105 transition-all flex items-center justify-center
                   ring-4 ring-[#003366]/20"
        aria-label="Abrir ayuda"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div data-testid="help-chat-panel"
             className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)]
                        h-[540px] max-h-[calc(100vh-8rem)] bg-white border border-slate-200
                        rounded-3xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 bg-[#003366] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} />
              <div>
                <div className="font-semibold text-sm">Asistente IQ-APP</div>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">Claude Sonnet 4.5</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-70" aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="help-chat-messages">
            {messages.length === 0 && (
              <div className="text-sm text-slate-500 text-center pt-6 px-4">
                <Sparkles size={20} className="mx-auto mb-2 text-[#EA580C]" />
                Hola. Pregúntame sobre normativa NEC/TIA/BICSI, cómo usar el Analyzer, conversiones de unidades, o cualquier duda mientras estimas.
                <div className="mt-4 space-y-1.5">
                  {[
                    "¿Cuál es el límite de longitud para CAT6 según TIA-568?",
                    "Diferencias entre NEC 2026 y NEC 2020 para conduit fill",
                    "¿Cómo se calcula el linear feet de cable?",
                  ].map((q, i) => (
                    <button key={i} onClick={() => setInput(q)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-xl
                                 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#003366] text-white rounded-br-md"
                    : "bg-slate-100 text-slate-900 rounded-bl-md"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-2.5">
                  <Loader2 size={14} className="animate-spin text-slate-500" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 flex items-end gap-2">
            <textarea
              data-testid="help-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Pregunta lo que sea…"
              rows={1}
              className="flex-1 resize-none border border-slate-300 rounded-xl px-3 py-2 text-sm
                         focus:outline-none focus:border-[#003366] max-h-32"
            />
            <button
              data-testid="help-chat-send"
              onClick={send}
              disabled={!input.trim() || sending}
              className="bg-[#EA580C] text-white p-2 rounded-xl hover:bg-[#c2410c] disabled:opacity-50">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
